import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fetchServerProgressionOverview } from "../api/progression";
import type {
  ProgressionBossRequirement,
  ProgressionExpansionSummary,
  ProgressionRaceEntry,
  ProgressionTierSummary,
} from "../api/types";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";
import { RankingsSectionNav } from "../components/rankings/RankingsSectionNav";
import { formatDateTime } from "../utils/format";
import { EQ_CLASS_NAMES, classToSlug } from "../api/eqClasses";
import {
  getActiveServerSlug,
  serverBossMetricPath,
  serverGuildPath,
} from "../utils/siteContext";

function sortedExpansions(expansions: ProgressionExpansionSummary[]) {
  return [...expansions].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

const BOSS_PAGE_SIZE = 50;

function displayExpansionName(expansion: ProgressionExpansionSummary) {
  return expansion.name === "Original EverQuest" ? "Classic" : expansion.name;
}

function tiersFor(expansion: ProgressionExpansionSummary) {
  return [...(expansion.tiers ?? expansion.raidTiers ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function objectivesFor(tier: ProgressionTierSummary) {
  return [...(tier.objectives ?? tier.raidObjectives ?? [])].sort(
    (a, b) =>
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
  );
}

function bossesFor(tier: ProgressionTierSummary) {
  const unique = new Map<string, ProgressionBossRequirement>();
  for (const objective of objectivesFor(tier)) {
    for (const boss of objective.requiredBosses ?? objective.bosses ?? []) {
      const id =
        boss.raidBossId ??
        boss.raidBoss?.id ??
        boss.boss?.id ??
        boss.id ??
        boss.slug ??
        boss.raidBoss?.name ??
        boss.boss?.name ??
        boss.name ??
        "unknown";
      if (!unique.has(id)) unique.set(id, boss);
    }
  }
  return [...unique.values()];
}

function bossId(boss: ProgressionBossRequirement) {
  return (
    boss.raidBossId ??
    boss.raidBoss?.id ??
    boss.boss?.id ??
    boss.id ??
    boss.slug ??
    null
  );
}

function bossSlug(boss: ProgressionBossRequirement) {
  return boss.raidBoss?.slug ?? boss.boss?.slug ?? boss.slug ?? null;
}

function slugify(value: string | number | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9`'._-]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function bossPathSlug(boss: ProgressionBossRequirement) {
  return bossSlug(boss) ?? slugify(bossName(boss)) ?? String(bossId(boss) ?? "");
}

function bossName(boss: ProgressionBossRequirement) {
  return boss.raidBoss?.name ?? boss.boss?.name ?? boss.name ?? "Unknown boss";
}

function bossZone(boss: ProgressionBossRequirement) {
  return (
    boss.raidBoss?.raidZone?.name ??
    boss.boss?.raidZone?.name ??
    boss.zone ??
    null
  );
}

function entryTime(entry: ProgressionRaceEntry) {
  return entry.completedAt ?? entry.killedAt ?? null;
}

function entryTimeMs(entry: ProgressionRaceEntry) {
  const time = entryTime(entry);
  const value = time ? Date.parse(time) : Number.POSITIVE_INFINITY;
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

interface ExpansionRankingRow {
  guildId: string;
  guildName: string;
  serverName: string | null;
  killedBossIds: Set<string>;
  progressTimeMs: number;
  progressTime: string | null;
}

function expansionBosses(expansion: ProgressionExpansionSummary) {
  const unique = new Map<string, ProgressionBossRequirement>();
  for (const tier of tiersFor(expansion)) {
    for (const boss of bossesFor(tier)) {
      const id = String(bossId(boss) ?? bossName(boss));
      if (!unique.has(id)) unique.set(id, boss);
    }
  }
  return [...unique.values()];
}

function expansionRankingRows(expansion: ProgressionExpansionSummary) {
  const rows = new Map<string, ExpansionRankingRow>();
  for (const boss of expansionBosses(expansion)) {
    const id = String(bossId(boss) ?? bossName(boss));
    for (const kill of boss.topKills ?? []) {
      const rawGuildId = kill.guild?.id;
      if (!rawGuildId) continue;
      const guildId = String(rawGuildId);
      const timeMs = entryTimeMs(kill);
      const existing = rows.get(guildId) ?? {
        guildId,
        guildName: kill.guild?.name ?? "Unknown guild",
        serverName: kill.server?.name ?? null,
        killedBossIds: new Set<string>(),
        progressTimeMs: Number.NEGATIVE_INFINITY,
        progressTime: null,
      };
      existing.killedBossIds.add(id);
      if (timeMs > existing.progressTimeMs && Number.isFinite(timeMs)) {
        existing.progressTimeMs = timeMs;
        existing.progressTime = entryTime(kill);
      }
      rows.set(guildId, existing);
    }
  }

  return [...rows.values()].sort((a, b) => {
    const progressDelta = b.killedBossIds.size - a.killedBossIds.size;
    if (progressDelta !== 0) return progressDelta;
    const aTime = Number.isFinite(a.progressTimeMs)
      ? a.progressTimeMs
      : Number.POSITIVE_INFINITY;
    const bTime = Number.isFinite(b.progressTimeMs)
      ? b.progressTimeMs
      : Number.POSITIVE_INFINITY;
    return aTime - bTime || a.guildName.localeCompare(b.guildName);
  });
}

function findExpansion(
  expansions: ProgressionExpansionSummary[],
  expansionSlug?: string,
) {
  return (
    sortedExpansions(expansions).find(
      (candidate) => candidate.slug === expansionSlug,
    ) ?? null
  );
}

function RankBadge({ rank }: { rank: number }) {
  const variant =
    rank === 1
      ? "gold"
      : rank === 2
        ? "silver"
        : rank === 3
          ? "bronze"
          : "plain";
  return <span className={`rank-badge rank-badge--${variant}`}>#{rank}</span>;
}

function findBoss(
  expansions: ProgressionExpansionSummary[],
  idOrSlug?: string,
  expansionSlug?: string,
) {
  for (const expansion of expansions) {
    if (expansionSlug && expansion.slug !== expansionSlug) continue;

    for (const tier of tiersFor(expansion)) {
      const boss = bossesFor(tier).find((candidate) => {
        const candidateId = bossId(candidate);
        const candidateSlug = bossPathSlug(candidate);
        return String(candidateId ?? "") === idOrSlug || candidateSlug === idOrSlug;
      });
      if (boss) return { expansion, tier, boss };
    }
  }
  return null;
}

export function ProgressionTierRankingsPage() {
  const { expansionSlug = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const serverSlug = getActiveServerSlug();
  const currentPage = Math.max(
    1,
    Number.parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );
  const pageSize = 50;
  const query = useQuery({
    queryKey: ["progression-expansion-rankings", serverSlug, expansionSlug],
    queryFn: () => fetchServerProgressionOverview(serverSlug),
    enabled: expansionSlug.length > 0,
  });

  const expansions = useMemo(
    () => sortedExpansions(query.data?.progression.expansions ?? []),
    [query.data],
  );
  const selectedExpansion = useMemo(
    () => findExpansion(expansions, expansionSlug),
    [expansions, expansionSlug],
  );
  const rows = selectedExpansion ? expansionRankingRows(selectedExpansion) : [];
  const totalBosses = selectedExpansion
    ? expansionBosses(selectedExpansion).length
    : 0;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pagedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  function goToPage(page: number) {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) {
      next.delete("page");
    } else {
      next.set("page", String(page));
    }
    setSearchParams(next);
  }

  const activeServerSlug = getActiveServerSlug(window.location.pathname);

  return (
    <>
      <section className="page-hero rankings-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">
            Progression Rankings
            <span className="page-hero__server d-block">
              {query.data?.server?.name ?? "Server"}
            </span>
          </h1>
        </div>
        {expansions.length > 0 ? (
          <div className="page-subnav rankings-expansion-subnav">
            <div className="content-wrapper">
              <ul
                className="nav secondary-tabs"
                aria-label="Progression rankings expansion scope"
              >
                {expansions.map((expansion) => (
                  <li className="nav-item" key={expansion.id}>
                    <Link
                      className={`nav-link ${selectedExpansion?.slug === expansion.slug ? "active" : ""}`}
                      to={`/rankings/progression/${encodeURIComponent(expansion.slug)}`}
                    >
                      {displayExpansionName(expansion)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        <RankingsSectionNav
          activeSection="progression"
          serverSlug={serverSlug}
          progressionExpansionSlug={selectedExpansion?.slug ?? expansionSlug}
        />
      </section>

      <div className="page-main">
        <div className="content-wrapper">
          {query.isLoading ? <LoadingState label="Loading rankings" /> : null}
          {query.isError ? (
            <ErrorState title="Could not load rankings" error={query.error} />
          ) : null}
          {query.data && !selectedExpansion ? (
            <div className="alert alert-secondary">Expansion not found.</div>
          ) : null}
          {selectedExpansion ? (
            <>
              <div className="card border-secondary-subtle bg-body-tertiary rankings-card rankings-table-card">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-dark table-sm table-hover align-middle mb-0 progression-race-table rankings-mini-table">
                      <thead>
                        <tr>
                          <th
                            className="text-body-secondary"
                            style={{ width: "4rem" }}
                          >
                            Rank
                          </th>
                          <th>Guild</th>
                          <th className="text-end">Progress</th>
                          <th className="text-end">Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedRows.map((row, index) => {
                          const rank = (safePage - 1) * pageSize + index + 1;
                          return (
                            <tr key={row.guildId}>
                              <td>
                                <RankBadge rank={rank} />
                              </td>
                              <td>
                                <Link
                                  to={serverGuildPath(activeServerSlug, {
                                    id: row.guildId,
                                    name: row.guildName,
                                  })}
                                  className="link-light fw-semibold text-decoration-none"
                                >
                                  {row.guildName}
                                </Link>
                              </td>
                              <td className="text-end tabular-nums">
                                {row.killedBossIds.size}/{totalBosses}
                              </td>
                              <td className="text-end text-nowrap">
                                {row.progressTime ? (
                                  formatDateTime(row.progressTime)
                                ) : (
                                  <span className="text-body-secondary">
                                    Unknown
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {rows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="text-body-secondary py-3"
                            >
                              No boss kill records yet.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {rows.length > pageSize ? (
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                  <div className="small text-body-secondary">
                    Showing {(safePage - 1) * pageSize + 1}-
                    {Math.min(safePage * pageSize, rows.length)} of{" "}
                    {rows.length}
                  </div>
                  <div
                    className="btn-group btn-group-sm"
                    role="group"
                    aria-label="Progression rankings pages"
                  >
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={safePage <= 1}
                      onClick={() => goToPage(safePage - 1)}
                    >
                      Previous
                    </button>
                    <span className="btn btn-outline-secondary disabled">
                      Page {safePage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={safePage >= totalPages}
                      onClick={() => goToPage(safePage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

function ClassLeaderboardLinks({
  serverSlug,
  bossId,
  bossName,
}: {
  serverSlug: string;
  bossId: string;
  bossName: string;
}) {
  const classButtons = [
    { label: "All Classes", slug: null },
    ...EQ_CLASS_NAMES.map((className) => ({
      label: className,
      slug: classToSlug(className),
    })),
  ];

  return (
    <div className="card border-secondary-subtle bg-body-tertiary mb-4">
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h2 className="h5 mb-1">Parse Leaderboards</h2>
            <p className="text-body-secondary mb-0">
              View {bossName} damage and healing parses by class. Unknown-class
              parses remain in All Classes.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Link
              className="btn btn-primary btn-sm"
              to={serverBossMetricPath(serverSlug, bossId, "DAMAGE")}
            >
              Damage
            </Link>
            <Link
              className="btn btn-outline-primary btn-sm"
              to={serverBossMetricPath(serverSlug, bossId, "HEALING")}
            >
              Healing
            </Link>
          </div>
        </div>

        <div className="d-grid gap-3">
          <div>
            <div className="small text-uppercase text-body-secondary fw-semibold mb-2">
              Damage by class
            </div>
            <div className="d-flex flex-wrap gap-2">
              {classButtons.map((item) => (
                <Link
                  key={`damage-${item.slug ?? "all"}`}
                  className="btn btn-sm btn-outline-secondary"
                  to={serverBossMetricPath(
                    serverSlug,
                    bossId,
                    "DAMAGE",
                    item.slug,
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="small text-uppercase text-body-secondary fw-semibold mb-2">
              Healing by class
            </div>
            <div className="d-flex flex-wrap gap-2">
              {classButtons.map((item) => (
                <Link
                  key={`healing-${item.slug ?? "all"}`}
                  className="btn btn-sm btn-outline-secondary"
                  to={serverBossMetricPath(
                    serverSlug,
                    bossId,
                    "HEALING",
                    item.slug,
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProgressionBossRankingsPage() {
  const {
    expansionSlug = "",
    bossSlug = "",
    bossId: legacyBossId = "",
  } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const serverSlug = getActiveServerSlug();
  const requestedBoss = bossSlug || legacyBossId;
  const currentPage = Math.max(
    1,
    Number.parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );
  const query = useQuery({
    queryKey: [
      "progression-boss-rankings",
      serverSlug,
      expansionSlug,
      requestedBoss,
    ],
    queryFn: () => fetchServerProgressionOverview(serverSlug),
    enabled: requestedBoss.length > 0,
  });

  const expansions = useMemo(
    () => sortedExpansions(query.data?.progression.expansions ?? []),
    [query.data],
  );
  const match = useMemo(
    () => findBoss(expansions, requestedBoss, expansionSlug || undefined),
    [expansions, requestedBoss, expansionSlug],
  );
  const selectedExpansion = match?.expansion ?? findExpansion(expansions, expansionSlug);
  const kills = [...(match?.boss.topKills ?? [])].sort(
    (a, b) => entryTimeMs(a) - entryTimeMs(b),
  );
  const totalPages = Math.max(1, Math.ceil(kills.length / BOSS_PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedKills = kills.slice(
    (safePage - 1) * BOSS_PAGE_SIZE,
    safePage * BOSS_PAGE_SIZE,
  );
  const activeServerSlug = getActiveServerSlug(window.location.pathname);

  function goToPage(page: number) {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) {
      next.delete("page");
    } else {
      next.set("page", String(page));
    }
    setSearchParams(next);
  }

  return (
    <>
      <section className="page-hero rankings-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">
            Progression Rankings
            <span className="page-hero__server d-block">
              {query.data?.server?.name ?? "Server"}
            </span>
          </h1>
          <p className="fs-3 mb-0 text-body-secondary mt-2">
            {match
              ? `${bossName(match.boss)}${bossZone(match.boss) ? ` · ${bossZone(match.boss)}` : ""}`
              : "Boss Progression"}
          </p>
        </div>
        {expansions.length > 0 ? (
          <div className="page-subnav rankings-expansion-subnav">
            <div className="content-wrapper">
              <ul
                className="nav secondary-tabs"
                aria-label="Progression rankings expansion scope"
              >
                {expansions.map((expansion) => (
                  <li className="nav-item" key={expansion.id}>
                    <Link
                      className={`nav-link ${selectedExpansion?.slug === expansion.slug ? "active" : ""}`}
                      to={`/rankings/progression/${encodeURIComponent(expansion.slug)}`}
                    >
                      {displayExpansionName(expansion)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        <RankingsSectionNav
          activeSection="progression"
          serverSlug={serverSlug}
          progressionExpansionSlug={selectedExpansion?.slug ?? expansionSlug}
        />
      </section>

      <div className="page-main">
        <div className="content-wrapper">
          {query.isLoading ? (
            <LoadingState label="Loading boss progression" />
          ) : null}
          {query.isError ? (
            <ErrorState
              title="Could not load boss progression"
              error={query.error}
            />
          ) : null}
          {query.data && !match ? (
            <div className="alert alert-secondary">Boss not found.</div>
          ) : null}
          {match ? (
            <>
              <div className="card border-secondary-subtle bg-body-tertiary rankings-card rankings-table-card">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-dark table-sm table-hover align-middle mb-0 progression-race-table rankings-mini-table">
                      <thead>
                        <tr>
                          <th className="text-body-secondary" style={{ width: "4rem" }}>
                            Rank
                          </th>
                          <th>Guild</th>
                          <th className="d-none d-md-table-cell">Server</th>
                          <th className="text-end">Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedKills.map((kill, index) => {
                          const rank = (safePage - 1) * BOSS_PAGE_SIZE + index + 1;
                          return (
                            <tr key={kill.id ?? `${kill.guild?.id ?? "guild"}-${index}`}>
                              <td>
                                <RankBadge rank={rank} />
                              </td>
                              <td>
                                {kill.guild?.id ? (
                                  <Link
                                    to={serverGuildPath(activeServerSlug, kill.guild)}
                                    className="link-light fw-semibold text-decoration-none"
                                  >
                                    {kill.guild.name}
                                  </Link>
                                ) : (
                                  <span className="fw-semibold">
                                    {kill.guild?.name ?? "Unknown guild"}
                                  </span>
                                )}
                              </td>
                              <td className="d-none d-md-table-cell text-body-secondary">
                                {kill.server?.name ?? "Unknown server"}
                              </td>
                              <td className="text-end text-nowrap">
                                {entryTime(kill) ? (
                                  formatDateTime(entryTime(kill) as string)
                                ) : (
                                  <span className="text-body-secondary">Unknown</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {kills.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-body-secondary py-3">
                              No boss kill records yet.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {kills.length > BOSS_PAGE_SIZE ? (
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                  <div className="small text-body-secondary">
                    Showing {(safePage - 1) * BOSS_PAGE_SIZE + 1}-
                    {Math.min(safePage * BOSS_PAGE_SIZE, kills.length)} of {kills.length}
                  </div>
                  <div
                    className="btn-group btn-group-sm"
                    role="group"
                    aria-label="Boss progression rankings pages"
                  >
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={safePage <= 1}
                      onClick={() => goToPage(safePage - 1)}
                    >
                      Previous
                    </button>
                    <span className="btn btn-outline-secondary disabled">
                      Page {safePage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={safePage >= totalPages}
                      onClick={() => goToPage(safePage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

